import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchArticles, fetchCategories } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ArticleCover } from "@/components/ArticleCover";
import { InFeedAd } from "@/components/AdSlot";

const formatDate = (iso) =>
    new Date(iso + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });

export const ArticleCard = ({ article, index }) => (
    <Link
        to={`/artigo/${article.slug}`}
        data-testid={`article-card-${article.slug}`}
        className="group flex flex-col border border-white/10 hover:border-white/25 transition-colors bg-paper"
    >
        <div className="overflow-hidden h-44">
            <div className="h-full transition-transform duration-700 ease-out group-hover:scale-105">
                <ArticleCover article={article} />
            </div>
        </div>
        <div className="flex-1 flex flex-col p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] mb-3">
                <span className="text-gold font-medium">{article.category}</span>
                <span className="text-slate-600">·</span>
                <span className="text-slate-500">{formatDate(article.published_at)}</span>
            </div>
            <h3 className="font-serif text-lg leading-snug group-hover:text-gold transition-colors clamp-2">
                {article.title}
            </h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed clamp-2">{article.excerpt}</p>
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-[11px] text-slate-500">
                <span>{article.read_minutes} min · {article.author}</span>
                <ArrowUpRight size={14} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </div>
    </Link>
);

export const ArticleExplorer = () => {
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [category, setCategory] = useState("Todos");

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(t);
    }, [query]);

    const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
    const { data: articles = [], isLoading } = useQuery({
        queryKey: ["articles", debouncedQuery, category],
        queryFn: () => fetchArticles({
            q: debouncedQuery || undefined,
            category: category !== "Todos" ? category : undefined,
        }),
    });

    const chips = useMemo(() => ["Todos", ...categories.map((c) => c.name)], [categories]);

    const gridItems = useMemo(() => {
        const items = [];
        articles.forEach((a, i) => {
            items.push(<Reveal key={a.slug} delay={(i % 3) * 0.07}><ArticleCard article={a} index={i} /></Reveal>);
            if (i === 2) items.push(<Reveal key="ad-infeed" delay={0.1}><InFeedAd /></Reveal>);
        });
        return items;
    }, [articles]);

    return (
        <section id="artigos" data-testid="article-explorer" className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
            <Reveal>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
                    <div>
                        <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">Biblioteca</span>
                        <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-3">Todos os artigos</h2>
                    </div>
                    <div className="relative w-full md:w-80">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            data-testid="article-search-input"
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Buscar por tema, título..."
                            className="w-full bg-paper border border-white/10 focus:border-gold/60 outline-none pl-11 pr-4 py-3 text-sm placeholder:text-slate-600 transition-colors"
                        />
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <div data-testid="category-filters" className="flex flex-wrap gap-2 mb-10 md:mb-14">
                    {chips.map((c) => (
                        <button
                            key={c}
                            data-testid={`category-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setCategory(c)}
                            className={`text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${category === c
                                ? "bg-gold text-ink border-gold font-medium"
                                : "border-white/15 text-slate-400 hover:border-gold/60 hover:text-gold"
                                }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </Reveal>

            {isLoading ? (
                <div data-testid="articles-loading" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="border border-white/5 bg-paper animate-pulse h-72" />
                    ))}
                </div>
            ) : articles.length === 0 ? (
                <div data-testid="articles-empty" className="border border-dashed border-white/15 py-20 text-center">
                    <p className="font-serif text-2xl text-slate-400 italic">Nenhum artigo encontrado.</p>
                    <p className="text-sm text-slate-600 mt-2">Tente outro termo ou limpe os filtros.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{gridItems}</div>
            )}
        </section>
    );
};
